import React, { useState, useEffect } from 'react';
import {
  Box, Button, Typography, Popover, IconButton, TextField,
  Autocomplete, Chip, Stack, Grid, Divider
} from '@mui/material';
import {
  FilterAlt as FilterAltIcon,
  Close as CloseIcon,
  EventNote as EventNoteIcon,
  Business as AgentIcon,
  Home as ProjectIcon,
  Person as LeadIcon
} from '@mui/icons-material';

import AgentSelect from '../../components/common/AgentSelect';

// Common styles for chips
const chipStyles = {
  height: '24px',
  '& .MuiChip-label': {
    fontSize: '0.75rem',
    px: 1,
    fontWeight: 400
  }
};

// Common styles for section headers
const sectionHeaderStyles = {
  fontSize: '0.75rem',
  color: 'text.secondary',
  fontWeight: 500,
  textTransform: 'none'
};

// Common styles for Autocomplete
const autocompleteStyles = {
  '& .MuiInputBase-root': { 
    fontSize: '0.75rem',
    minHeight: '32px',
    fontWeight: 400
  },
  '& .MuiInputBase-input': {
    fontSize: '0.75rem',
    fontWeight: 400
  },
  '& .MuiAutocomplete-endAdornment': {
    top: '50%',
    transform: 'translateY(-50%)'
  },
  '& .MuiAutocomplete-popupIndicator': {
    padding: '2px'
  },
  '& .MuiAutocomplete-clearIndicator': {
    padding: '2px'
  }
};

const BookingFilter = ({
  open = false,
  anchorEl = null,
  filters: initialFilters = {},
  setInitialFilters,
  agents = [],
  leads = [],
  projects = [],
  booking_statuses = [],
  payment_statuses = [],
  onClose,
  onApply
}) => {
  const [filters, setFilters] = useState({
    agents: [],
    leads: [],
    projects: [],
    booking_statuses: [],
    payment_statuses: [],
    ...initialFilters
  });

  const [hasChanges, setHasChanges] = useState(false);

  // Sync filters with applied filters when modal opens
  useEffect(() => {
    if (open) {
      // Normalize applied filters to match UI expectations
      const normalizedFilters = { ...initialFilters };

      // Array fields that should always be arrays in the UI
      const arrayFields = ['agents', 'leads', 'projects', 'booking_statuses', 'payment_statuses'];

      arrayFields.forEach(field => {
        if (normalizedFilters[field] !== undefined && normalizedFilters[field] !== null) {
          // Convert single values to arrays
          if (!Array.isArray(normalizedFilters[field])) {
            normalizedFilters[field] = [normalizedFilters[field]];
          }
        }
      });

      // When modal opens, reset the filter state to match applied filters
      setFilters(normalizedFilters || {});
      setHasChanges(false);
    }
  }, [open, initialFilters, setInitialFilters]);

  // Add effect to check for changes whenever filters change
  useEffect(() => {
    // Deep comparison of filters
    const initialKeys = Object.keys(initialFilters);
    const currentKeys = Object.keys(filters);

    // Check if keys are different
    if (initialKeys.length !== currentKeys.length) {
      setHasChanges(true);
      return;
    }

    // Check if values are different
    let changed = false;

    for (const key of currentKeys) {
      const initialValue = initialFilters[key];
      const currentValue = filters[key];

      if (Array.isArray(currentValue)) {
        // Compare arrays
        if (!initialValue || !Array.isArray(initialValue) ||
          initialValue.length !== currentValue.length ||
          !initialValue.every((val, i) => val === currentValue[i])) {
          changed = true;
          break;
        }
      } else {
        // Compare other values
        if (initialValue !== currentValue) {
          changed = true;
          break;
        }
      }
    }

    setHasChanges(changed);
  }, [filters, initialFilters]);


  const handleChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value,
    }));
    setHasChanges(true);
  };

  const handleApply = () => {
    onApply(filters);
    setHasChanges(false);
  };

  const handleClear = () => {
    const clearedFilters = {};
    setFilters(clearedFilters);
    setHasChanges(true);
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={handleClose}
      PaperProps={{
        sx: {
          width: 500,
          maxHeight: '90vh',
          p: 0,
          borderRadius: 1,
          overflow: 'hidden'
        }
      }}
    >
      {/* Header */}
      <Box sx={{ 
        px: 2,
        py: 1.5,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(0, 0, 0, 0.12)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <FilterAltIcon sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="subtitle1" fontWeight={600}>
            Booking Filters
          </Typography>
        </Box>
        <IconButton size="small" onClick={handleClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      {/* Filters List */}
      <Box sx={{ overflow: 'auto', flex: 1 }}>
        <>
          {/* Agent Filters */}
          {agents && agents.length > 0 && (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                position: 'relative',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.01)'
                }
              }}
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 1,
                justifyContent: 'space-between'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <AgentIcon fontSize="small" />
                  <Typography sx={{
                    ml: 1,
                    ...sectionHeaderStyles,
                    fontWeight: 600,
                    color: 'text.primary'
                  }}>
                    Agents
                  </Typography>
                </Box>

                {/* Show clear button if filter is active */}
                {filters.agents && filters.agents.length > 0 && (
                  <IconButton
                    size="small"
                    sx={{ p: 0.5 }}
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters.agents;
                      setFilters(newFilters);
                      setHasChanges(true);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <AgentSelect
                multiple={true}
                value={Array.isArray(filters.agents) ? filters.agents : []}
                onChange={(agents) => {
                  handleChange('agents', agents);
                }}
                label="Select Agents"
                size="small"
                sx={{
                  mt: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    fontSize: '0.75rem',
                    '&.Mui-focused': {
                      boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.1)'
                    }
                  }
                }}
                agents={agents}
              />
            </Box>
          )}

          {/* Lead Filters */}
          {leads && leads.length > 0 && (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                position: 'relative',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.01)'
                }
              }}
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 1,
                justifyContent: 'space-between'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <LeadIcon fontSize="small" />
                  <Typography sx={{
                    ml: 1,
                    ...sectionHeaderStyles,
                    fontWeight: 600,
                    color: 'text.primary'
                  }}>
                    Leads
                  </Typography>
                </Box>

                {/* Show clear button if filter is active */}
                {filters.leads && filters.leads.length > 0 && (
                  <IconButton
                    size="small"
                    sx={{ p: 0.5 }}
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters.leads;
                      setFilters(newFilters);
                      setHasChanges(true);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Autocomplete
                multiple
                size="small"
                options={leads}
                value={Array.isArray(filters.leads) ? filters.leads : []}
                onChange={(e, newValue) => handleChange('leads', newValue)}
                getOptionKey={(option) => option?.value || option?.id || option?.lead_id || option?.label || Math.random()}
                getOptionLabel={(option) => option?.label || option?.name || ''}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                sx={{
                  ...autocompleteStyles,
                  mt: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    '&.Mui-focused': {
                      boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.1)'
                    }
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select leads..."
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      label={option?.label || option?.name || ''}
                      size="small"
                      sx={{
                        ...chipStyles,
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.12)'
                        }
                      }}
                    />
                  ))
                }
              />
            </Box>
          )}

          {/* Project Filters */}
          {projects && projects.length > 0 && (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                position: 'relative',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.01)'
                }
              }}
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 1,
                justifyContent: 'space-between'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <ProjectIcon fontSize="small" />
                  <Typography sx={{
                    ml: 1,
                    ...sectionHeaderStyles,
                    fontWeight: 600,
                    color: 'text.primary'
                  }}>
                    Projects
                  </Typography>
                </Box>

                {/* Show clear button if filter is active */}
                {filters.projects && filters.projects.length > 0 && (
                  <IconButton
                    size="small"
                    sx={{ p: 0.5 }}
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters.projects;
                      setFilters(newFilters);
                      setHasChanges(true);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Autocomplete
                multiple
                size="small"
                options={projects}
                value={Array.isArray(filters.projects) ? filters.projects : []}
                onChange={(e, newValue) => handleChange('projects', newValue)}
                getOptionKey={(option) => option?.value || option?.id || option?.label || Math.random()}
                getOptionLabel={(option) => option?.label || option?.name || ''}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                sx={{
                  ...autocompleteStyles,
                  mt: 1,
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '6px',
                    '&.Mui-focused': {
                      boxShadow: '0 0 0 2px rgba(25, 118, 210, 0.1)'
                    }
                  }
                }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Select projects..."
                    variant="outlined"
                    size="small"
                    fullWidth
                  />
                )}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      label={option?.label || option?.name || ''}
                      size="small"
                      sx={{
                        ...chipStyles,
                        backgroundColor: 'rgba(25, 118, 210, 0.08)',
                        borderRadius: '4px',
                        '&:hover': {
                          backgroundColor: 'rgba(25, 118, 210, 0.12)'
                        }
                      }}
                    />
                  ))
                }
              />
            </Box>
          )}

          {/* Booking Status Filters */}
          {booking_statuses && booking_statuses.length > 0 && (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                position: 'relative',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.01)'
                }
              }}
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 1,
                justifyContent: 'space-between'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <EventNoteIcon fontSize="small" />
                  <Typography sx={{
                    ml: 1,
                    ...sectionHeaderStyles,
                    fontWeight: 600,
                    color: 'text.primary'
                  }}>
                    Booking Status
                  </Typography>
                </Box>

                {/* Show clear button if filter is active */}
                {filters.booking_statuses && filters.booking_statuses.length > 0 && (
                  <IconButton
                    size="small"
                    sx={{ p: 0.5 }}
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters.booking_statuses;
                      setFilters(newFilters);
                      setHasChanges(true);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {booking_statuses.map((status) => {
                  const selected = Array.isArray(filters.booking_statuses) && filters.booking_statuses.includes(status.value);
                  return (
                    <Chip
                      key={status.value}
                      label={status.label}
                      size="small"
                      onClick={() => {
                        const currentStatus = Array.isArray(filters.booking_statuses) ? filters.booking_statuses : [];
                        if (selected) {
                          handleChange('booking_statuses', currentStatus.filter(s => s !== status.value));
                        } else {
                          handleChange('booking_statuses', [...currentStatus, status.value]);
                        }
                      }}
                      sx={{
                        bgcolor: selected ? '#FFA726' : '#f5f5f5',
                        color: selected ? '#fff' : '#222',
                        border: 'none',
                        fontSize: '0.7rem',
                        height: '24px',
                        borderRadius: 2,
                        fontWeight: 500,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        '& .MuiChip-label': { px: 1 },
                        mb: 0.5,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: selected ? '#FFA726' : '#e0e0e0',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}

          {/* Payment Status Filters */}
          {payment_statuses && payment_statuses.length > 0 && (
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: '1px solid rgba(0, 0, 0, 0.08)',
                position: 'relative',
                '&:hover': {
                  bgcolor: 'rgba(0, 0, 0, 0.01)'
                }
              }}
            >
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                mb: 1,
                justifyContent: 'space-between'
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <EventNoteIcon fontSize="small" />
                  <Typography sx={{
                    ml: 1,
                    ...sectionHeaderStyles,
                    fontWeight: 600,
                    color: 'text.primary'
                  }}>
                    Payment Status
                  </Typography>
                </Box>

                {/* Show clear button if filter is active */}
                {filters.payment_statuses && filters.payment_statuses.length > 0 && (
                  <IconButton
                    size="small"
                    sx={{ p: 0.5 }}
                    onClick={() => {
                      const newFilters = { ...filters };
                      delete newFilters.payment_statuses;
                      setFilters(newFilters);
                      setHasChanges(true);
                    }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
              </Box>

              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 1 }}>
                {payment_statuses.map((ps) => {
                  const selected = Array.isArray(filters.payment_statuses) && filters.payment_statuses.includes(ps.value);
                  return (
                    <Chip
                      key={ps.value}
                      label={ps.label}
                      size="small"
                      onClick={() => {
                        const currentPaymentStatus = Array.isArray(filters.payment_statuses) ? filters.payment_statuses : [];
                        if (selected) {
                          handleChange('payment_statuses', currentPaymentStatus.filter(s => s !== ps.value));
                        } else {
                          handleChange('payment_statuses', [...currentPaymentStatus, ps.value]);
                        }
                      }}
                      sx={{
                        bgcolor: selected ? '#66BB6A' : '#f5f5f5',
                        color: selected ? '#fff' : '#222',
                        border: 'none',
                        fontSize: '0.7rem',
                        height: '24px',
                        borderRadius: 2,
                        fontWeight: 500,
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                        '& .MuiChip-label': { px: 1 },
                        mb: 0.5,
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          bgcolor: selected ? '#66BB6A' : '#e0e0e0',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                        }
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          )}
        </>
      </Box>

      {/* Footer Buttons */}
      <Box sx={{
        p: 2,
        borderTop: '1px solid rgba(0, 0, 0, 0.12)',
        display: 'flex',
        justifyContent: 'flex-end',
        bgcolor: '#fff'
      }}>
        <Button
          size="small"
          variant="outlined"
          onClick={handleClear}
          sx={{ mr: 1, borderRadius: 2 }}
          // Only enable if there are filters to clear
          disabled={Object.keys(filters).length === 0}
        >
          Clear
        </Button>
        <Button
          size="small"
          variant="contained"
          color="success"
          onClick={handleApply}
          sx={{
            bgcolor: '#00875A',
            '&:hover': { bgcolor: '#006644' },
            borderRadius: 2
          }}
          // Enable if there are any filters selected
          disabled={Object.keys(filters).length === 0}
        >
          Apply
        </Button>
      </Box>
    </Popover>
  );
};

export default BookingFilter;